import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, Loader2, Play, RotateCcw, XCircle } from 'lucide-react'
import {
  apiFetch,
  createGatewayConfig,
  getGatewayConfigs,
  getVideoModels,
  type GatewayConfig,
} from '../../api/client'
import {
  getTypedNodeData,
  type GeneratedVideo,
  type ReactFlowCanvasNodeDataByType,
} from '../../generated/nodeDataTypes'
import {
  type ReactFlowCanvasEdge,
} from '../../hooks/useYCanvas'
import { notifyGatewayConfigChanged } from '../../hooks/useVideoModels'
import { useTranslation } from 'react-i18next'
import type {
  ReplayCheckDefinition,
  ReplayCheckId,
  ReplayCheckResult,
  ReplayCheckState,
  ReplayCheckStatus,
  ReplayOperationCardsProps,
  ReplayRunnerContext,
} from './replayOperationContract'

const VIDEO_NODE_ID = 'replay_video_1'
const AUDIO_NODE_ID = 'replay_audio_1'
const EDITOR_NODE_ID = 'replay_editor_1'
const DELETE_PROBE_NODE_ID = 'replay_delete_probe_video'
const OBJECT_NODE_ID = 'replay_object_1'
const LOCATION_NODE_ID = 'replay_location_1'
const REQUIRED_NODE_IDS: string[] = [OBJECT_NODE_ID, LOCATION_NODE_ID, VIDEO_NODE_ID, AUDIO_NODE_ID, EDITOR_NODE_ID]
const REQUIRED_EDGE_ENDPOINTS: Array<[source: string, target: string]> = [
  [VIDEO_NODE_ID, EDITOR_NODE_ID],
  [AUDIO_NODE_ID, EDITOR_NODE_ID],
]
const REPLAY_BYOM_MODEL_NAME = 'Replay E2E Model'
const REPLAY_BYOM_CAPABILITIES = {
  duration_range: ['5', '8', '10'],
  aspect_ratios: ['16:9', '9:16'],
  resolutions: ['720p', '1080p'],
  audio_control: ['with_audio', 'no_audio'],
  reference_images_support: ['1', '2', '3', '4'],
  request_pattern: 'pattern_1',
}
const POLL_INTERVAL_MS = 250
const CANVAS_GRAPH_TIMEOUT_MS = 45_000
const JSONB_VALIDATOR_TIMEOUT_MS = 60_000
const ASSET_CHECK_TIMEOUT_MS = 90_000
const UI_CHECK_TIMEOUT_MS = 30_000
// Unified audio derives lanes through a network-backed route, so audio checks
// need more time than purely local UI checks.
const AUDIO_CHECK_TIMEOUT_MS = 60_000
const MEDIA_DOM_RENDER_GRACE_MS = 2_000
const TIMELINE_SCROLL_CHECK_TIMEOUT_MS = 60_000
const CANVAS_TAB_OPERATION_TITLE = 'Replay Canvas Operation'
const CANVAS_TAB_TEST_ID_PREFIX = 'canvas-tab-'
const DIRECTOR_CREATED_CANVAS_ID = 'canvas-replay-2'
const DIRECTOR_CREATED_CANVAS_TITLE = 'Canvas 2'

type ReplayCanvasSummary = {
  id: string
  title: string
}

type ReplayCanvasListResponse = {
  canvases: ReplayCanvasSummary[]
}

class ReplayCheckError extends Error {
  readonly status: 'failed' | 'blocked'
  readonly observed: string
  readonly details: string[]

  constructor(status: 'failed' | 'blocked', message: string, observed: string, details: string[] = []) {
    super(message)
    this.name = 'ReplayCheckError'
    this.status = status
    this.observed = observed
    this.details = details
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function nodeSelector(nodeId: string): string {
  return `.react-flow__node[data-id="${nodeId}"]`
}

function queryHtml(selector: string, root: ParentNode = document): HTMLElement | null {
  const element = root.querySelector(selector)
  return element instanceof HTMLElement ? element : null
}

function queryElement(selector: string, root: ParentNode = document): Element | null {
  return root.querySelector(selector)
}

function queryButton(selector: string, root: ParentNode = document): HTMLButtonElement | null {
  const element = root.querySelector(selector)
  return element instanceof HTMLButtonElement ? element : null
}

function queryInput(selector: string, root: ParentNode = document): HTMLInputElement | null {
  const element = root.querySelector(selector)
  return element instanceof HTMLInputElement ? element : null
}

function queryVideo(selector: string, root: ParentNode = document): HTMLVideoElement | null {
  const element = root.querySelector(selector)
  return element instanceof HTMLVideoElement ? element : null
}

function queryVideos(selector: string, root: ParentNode = document): HTMLVideoElement[] {
  return Array.from(root.querySelectorAll(selector)).filter(
    (element): element is HTMLVideoElement => element instanceof HTMLVideoElement,
  )
}

function isVisibleElement(element: HTMLElement | null): boolean {
  if (!element) return false
  const rect = element.getBoundingClientRect()
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  )
}

type ReplayVideoDomMatch = {
  video: HTMLVideoElement
  selector: string
}

export type ReplayVideoQueryOptions = {
  requireReplayVideoNodeSrc?: boolean
}

const GENERATED_VIDEO_SELECTORS: readonly string[] = [
  '[data-testid="video-preview-player"] video',
  '[data-testid="video-generator-flat-node"] video',
  '[data-flat-idle-node-preview="true"] video',
  'video[src]',
]

function isReplayVideoNodeSrc(src: string | undefined): boolean {
  if (!src) return false
  return src.includes(`/clips/${VIDEO_NODE_ID}/`) || src.includes(`%2Fclips%2F${VIDEO_NODE_ID}%2F`)
}

function matchesReplayVideoQuery(video: HTMLVideoElement, options: ReplayVideoQueryOptions): boolean {
  if (!options.requireReplayVideoNodeSrc) return true
  return isReplayVideoNodeSrc(videoSrcOf(video))
}

function queryGeneratedReplayVideoMatch(
  root: ParentNode = document,
  options: ReplayVideoQueryOptions = {},
): ReplayVideoDomMatch | null {
  for (const selector of GENERATED_VIDEO_SELECTORS) {
    const video = queryVideos(selector, root).find((candidate) => matchesReplayVideoQuery(candidate, options)) ?? null
    if (video) return { video, selector }
  }
  return null
}

export function queryGeneratedReplayVideoElement(
  root: ParentNode = document,
  options: ReplayVideoQueryOptions = {},
): HTMLVideoElement | null {
  return queryGeneratedReplayVideoMatch(root, options)?.video ?? null
}

function nodeElement(nodeId: string): HTMLElement | null {
  return queryHtml(nodeSelector(nodeId))
}

function titleOf(button: HTMLButtonElement | null): string {
  return button?.getAttribute('title') ?? ''
}

function elementText(element: HTMLElement | null): string {
  return element?.textContent?.replace(/\s+/g, ' ').trim() ?? ''
}

function containsText(element: HTMLElement | null, text: string): boolean {
  return elementText(element).toLowerCase().includes(text.toLowerCase())
}

export function canvasIdFromTabTestId(testId: string | null): string | null {
  if (!testId?.startsWith(CANVAS_TAB_TEST_ID_PREFIX)) return null
  const canvasId = testId.slice(CANVAS_TAB_TEST_ID_PREFIX.length)
  return canvasId ? canvasId : null
}

function findCanvasTabByTitle(title: string): HTMLButtonElement | null {
  const tabs = Array.from(
    document.querySelectorAll<HTMLButtonElement>(
      `[data-testid^="${CANVAS_TAB_TEST_ID_PREFIX}"]`,
    ),
  ).filter((element) => !element.getAttribute('data-testid')?.startsWith('canvas-tab-delete-'))
  return tabs.find((tab) => elementText(tab) === title) ?? null
}

function describeCanvasTabs(): string {
  const tabs = Array.from(
    document.querySelectorAll<HTMLButtonElement>(
      `[data-testid^="${CANVAS_TAB_TEST_ID_PREFIX}"]`,
    ),
  ).filter((element) => !element.getAttribute('data-testid')?.startsWith('canvas-tab-delete-'))
  return tabs
    .map((tab) => `${tab.getAttribute('data-testid') ?? 'unknown'}:${elementText(tab)}`)
    .join(', ') || 'none'
}

async function readJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiFetch(path, init)
  const body = await response.text()
  if (!response.ok) {
    throw new ReplayCheckError(
      'blocked',
      `Replay API request failed: ${path}`,
      `${response.status} ${response.statusText}`,
      [body.slice(0, 500) || 'empty response body'],
    )
  }
  try {
    return JSON.parse(body)
  } catch (error) {
    throw new ReplayCheckError(
      'blocked',
      `Replay API response was not JSON: ${path}`,
      error instanceof Error ? error.message : String(error),
      [body.slice(0, 500)],
    )
  }
}

async function readReplayCanvasList(projectId: string): Promise<ReplayCanvasListResponse> {
  return readJson<ReplayCanvasListResponse>(`/projects/${projectId}/workspaces/canvases`)
}

async function waitForElementGone(
  label: string,
  reader: () => HTMLElement | null,
  timeoutMs: number,
): Promise<void> {
  const gone = await waitForCondition(() => reader() === null, timeoutMs)
  if (!gone) {
    throw new ReplayCheckError('failed', `${label} did not close`, `${label} still visible after ${timeoutMs}ms`)
  }
}

async function ensureReplayGatewayConfig(): Promise<GatewayConfig> {
  const configs = await getGatewayConfigs()
  const existing = configs.find(
    (config) =>
      config.key_name === REPLAY_BYOM_MODEL_NAME &&
      config.enabled &&
      config.is_default,
  )
  if (existing) {
    return existing
  }

  const created = await createGatewayConfig({
    api_key: 'replay-e2e-local-key',
    base_url: 'https://queue.fal.run/replay/e2e-video-model',
    key_name: REPLAY_BYOM_MODEL_NAME,
    capabilities: REPLAY_BYOM_CAPABILITIES,
    is_default: true,
  })
  notifyGatewayConfigChanged()
  return created
}

async function openProjectSettings(context: ReplayRunnerContext): Promise<HTMLElement> {
  const settingsButton = await waitForElement(
    'project settings button',
    () => queryButton('[data-testid="toolbox-project-config"]'),
    remainingCheckMs(context, 10_000),
  )
  settingsButton.click()
  return waitForElement(
    'project settings modal',
    () => queryHtml('[data-testid="project-settings-modal"]'),
    remainingCheckMs(context, 10_000),
  )
}

async function closeProjectSettings(): Promise<void> {
  const modal = queryHtml('[data-testid="project-settings-modal"]')
  const submit = modal?.querySelector('button[type="submit"]')
  if (submit instanceof HTMLButtonElement) submit.click()
  await waitForElementGone(
    'project settings modal',
    () => queryHtml('[data-testid="project-settings-modal"]'),
    5_000,
  ).catch(() => undefined)
}

async function openVideoConfigPanel(context: ReplayRunnerContext): Promise<HTMLElement> {
  await focusReplayNode(context, VIDEO_NODE_ID)
  const videoNode = await waitForElement(
    'generated video node',
    () => nodeElement(VIDEO_NODE_ID),
    remainingCheckMs(context, 15_000),
  )
  const configButton = await waitForElement(
    'video node Config affordance',
    () => queryButton('[data-testid="quick-info-config"]', videoNode),
    remainingCheckMs(context, 10_000),
  )
  configButton.click()
  return waitForElement(
    'video config bottom panel',
    () => queryHtml('[data-testid="unified-bottom-panel"]', videoNode),
    remainingCheckMs(context, 10_000),
  )
}

function setYDocNodeField(nodeId: string, field: string, value: string): void {
  const devSetResult = window.__dwSetNodeField?.(nodeId, field, value)
  if (devSetResult === 'ok') return
  if (devSetResult) {
    throw new ReplayCheckError(
      'blocked',
      'Replay could not update the canvas node through the dev hook',
      devSetResult,
      [`node=${nodeId}`, `field=${field}`],
    )
  }

  throw new ReplayCheckError(
    'blocked',
    'Replay could not update the canvas node because the dev hook is unavailable',
    `missing __dwSetNodeField for ${nodeId}`,
    [`field=${field}`],
  )
}

type FlowBox = {
  id: string
  x: number
  y: number
  width: number
  height: number
}

async function measureFlowBox(context: ReplayRunnerContext, nodeId: string): Promise<FlowBox> {
  await focusReplayNode(context, nodeId)
  const node = await waitForElement(
    `${nodeId} canvas node`,
    () => nodeElement(nodeId),
    remainingCheckMs(context, 10_000),
  )
  const debugNode = window.__dwGetCanvasNodes?.().find(
    (candidate) => candidate.id === nodeId,
  )
  if (!debugNode) {
    throw new ReplayCheckError(
      'blocked',
      'Replay could not read canvas node position',
      `${nodeId} missing from __dwGetCanvasNodes`,
    )
  }
  return {
    id: nodeId,
    x: debugNode.x,
    y: debugNode.y,
    width: node.offsetWidth,
    height: node.offsetHeight,
  }
}

function flowBoxesOverlap(a: FlowBox, b: FlowBox): boolean {
  const xOverlap = a.x < b.x + b.width && a.x + a.width > b.x
  const yOverlap = a.y < b.y + b.height && a.y + a.height > b.y
  return xOverlap && yOverlap
}

async function waitForCondition(
  predicate: () => boolean | Promise<boolean>,
  timeoutMs: number,
): Promise<boolean> {
  const startedAt = performance.now()
  while (performance.now() - startedAt < timeoutMs) {
    if (await predicate()) return true
    await delay(POLL_INTERVAL_MS)
  }
  return predicate()
}

async function waitForElement<T extends HTMLElement>(
  label: string,
  reader: () => T | null,
  timeoutMs: number,
): Promise<T> {
  let lastValue: T | null = null
  const ok = await waitForCondition(() => {
    lastValue = reader()
    return lastValue !== null
  }, timeoutMs)
  if (!ok || !lastValue) {
    throw new ReplayCheckError('blocked', `${label} was not found`, `${label} missing after ${timeoutMs}ms`)
  }
  return lastValue
}

async function focusReplayNode(context: ReplayRunnerContext, nodeId: string): Promise<string> {
  const devFocusResult = window.__dwFocusNode?.(nodeId, { maxZoom: 0.72, yOffset: -120 })
  if (devFocusResult !== 'ok') {
    context.onFocusNode?.(nodeId)
  }
  await delay(450)
  const node = nodeElement(nodeId)
  node?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))
  if (node) await delay(150)
  return devFocusResult ?? 'workspace focus requested'
}

function missingRequiredNodes(canvasNodeIds: Set<string>): string[] {
  return REQUIRED_NODE_IDS.filter((nodeId) => !canvasNodeIds.has(nodeId))
}

function missingRequiredEdges(canvasEdges: ReactFlowCanvasEdge[]): string[] {
  return REQUIRED_EDGE_ENDPOINTS
    .filter(([source, target]) => !canvasEdges.some((edge) => edge.source === source && edge.target === target))
    .map(([source, target]) => `${source}->${target}`)
}

export type DanglingReplayEdge = {
  id: string
  source: string
  target: string
  missing: string[]
}

export function findDanglingReplayEdges(
  canvasNodeIds: Set<string>,
  canvasEdges: ReactFlowCanvasEdge[],
): DanglingReplayEdge[] {
  return canvasEdges.flatMap((edge) => {
    const missing: string[] = []
    if (!canvasNodeIds.has(edge.source)) missing.push(`source:${edge.source}`)
    if (!canvasNodeIds.has(edge.target)) missing.push(`target:${edge.target}`)
    if (missing.length === 0) return []
    return [{
      id: edge.id,
      source: edge.source,
      target: edge.target,
      missing,
    }]
  })
}

function describeDanglingEdges(danglingEdges: DanglingReplayEdge[]): string {
  return danglingEdges
    .map((edge) => `${edge.id} ${edge.source}->${edge.target} missing ${edge.missing.join(',')}`)
    .join('; ')
}

export type ReplayVideoNodeState = {
  nodePresent: boolean
  status: string
  error?: string
  hasOutVideo: boolean
  outVideoShape: string
  historyCount: number
  pickedIndex: number
  videoPath?: string
}

export type ReplayVideoDomTarget = 'element' | 'src'

export type ReplayVideoDomFailureTiming = {
  mediaReadyElapsedMs: number
  terminalMissingOutputElapsedMs: number
}

export type ReplayVideoDomSnapshot = {
  scope: 'node' | 'document'
  scopePresent: boolean
  videoPresent: boolean
  videoSrc?: string
  matchedSelector?: string
  videoCount: number
}

type ReplayVideoDomFailure = {
  message: string
  observed: string
  details: string[]
}

type ReplayVideoStateTimers = {
  mediaReadyAt: number | null
  terminalMissingOutputAt: number | null
}

type ReplayReadableNodeData = Partial<ReactFlowCanvasNodeDataByType['videoGenerator']>

function videoHistoryOf(value: ReplayReadableNodeData['out_video']): GeneratedVideo[] {
  if (value === null || value === undefined) return []
  return Array.isArray(value.history) ? value.history : []
}

function pickedIndexOf(value: ReplayReadableNodeData['out_video']): number {
  if (value === null || value === undefined) return 0
  return typeof value.picked_index === 'number' && Number.isFinite(value.picked_index)
    ? value.picked_index
    : 0
}

function shapeOf(value: ReplayReadableNodeData['out_video']): string {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (Array.isArray(value)) return 'array'
  if (typeof value === 'object') {
    const constructorName = value.constructor?.name
    return constructorName || 'object'
  }
  return typeof value
}

function readVideoPathFromHistoryEntry(entry: GeneratedVideo | undefined): string | undefined {
  const localPath = entry?.result?.local_path
  return typeof localPath === 'string' ? localPath : undefined
}

export function readReplayVideoNodeStateFromData(data?: ReplayReadableNodeData): ReplayVideoNodeState {
  const statusValue = data?.status
  const errorValue = data?.error
  const outVideo = data?.out_video
  const history = videoHistoryOf(outVideo)
  const pickedIndex = pickedIndexOf(outVideo)
  const selectedEntry = history[pickedIndex] ?? history[0]

  return {
    nodePresent: data !== null && data !== undefined,
    status: typeof statusValue === 'string' ? statusValue : 'unknown',
    error: typeof errorValue === 'string' ? errorValue : undefined,
    hasOutVideo: outVideo !== null && outVideo !== undefined,
    outVideoShape: shapeOf(outVideo),
    historyCount: history.length,
    pickedIndex,
    videoPath: readVideoPathFromHistoryEntry(selectedEntry),
  }
}

function readReplayVideoNodeState(context: ReplayRunnerContext): ReplayVideoNodeState {
  const node = context.getCanvasNode(VIDEO_NODE_ID)
  const data = node ? getTypedNodeData(node, 'videoGenerator') : undefined
  return readReplayVideoNodeStateFromData(data)
}

function describeReplayVideoNodeState(state: ReplayVideoNodeState): string {
  return [
    `node=${state.nodePresent ? 'present' : 'missing'}`,
    `status=${state.status}`,
    `out_video=${state.hasOutVideo ? state.outVideoShape : 'missing'}`,
    `history=${state.historyCount}`,
    `picked_index=${state.pickedIndex}`,
    `videoPath=${state.videoPath ?? 'missing'}`,
    `error=${state.error ?? 'none'}`,
  ].join(' ')
}

function videoSrcOf(video: HTMLVideoElement | null): string | undefined {
  const src = video?.currentSrc || video?.src || video?.getAttribute('src') || ''
  return src || undefined
}

function readReplayVideoDomSnapshot(
  root: ParentNode | null,
  scope: ReplayVideoDomSnapshot['scope'],
  match: ReplayVideoDomMatch | null = root ? queryGeneratedReplayVideoMatch(root) : null,
): ReplayVideoDomSnapshot {
  return {
    scope,
    scopePresent: root !== null,
    videoPresent: match !== null,
    videoSrc: videoSrcOf(match?.video ?? null),
    matchedSelector: match?.selector,
    videoCount: root?.querySelectorAll('video').length ?? 0,
  }
}

function describeReplayVideoDomSnapshot(snapshot: ReplayVideoDomSnapshot): string {
  const prefix = snapshot.scope === 'node' ? 'node' : 'document'
  return [
    `${prefix}_dom=${snapshot.scopePresent ? 'present' : 'missing'}`,
    `${prefix}_video=${snapshot.videoPresent ? 'present' : 'missing'}`,
    `${prefix}_video_count=${snapshot.videoCount}`,
    `${prefix}_selector=${snapshot.matchedSelector ?? 'none'}`,
    `${prefix}_videoSrc=${snapshot.videoSrc ?? 'missing'}`,
  ].join(' ')
}

export function buildReplayVideoBlockedDetails(
  state: ReplayVideoNodeState,
  nodeDom: ReplayVideoDomSnapshot,
  documentDom: ReplayVideoDomSnapshot,
  target: ReplayVideoDomTarget,
): string[] {
  const details = [
    `reactflow_snapshot: ${describeReplayVideoNodeState(state)}`,
    `node_dom_snapshot: ${describeReplayVideoDomSnapshot(nodeDom)}`,
    `document_dom_snapshot: ${describeReplayVideoDomSnapshot(documentDom)}`,
  ]

  if (!state.nodePresent) {
    details.push('The replay card cannot find replay_video_1 in the ReactFlow canvasNodes snapshot.')
  } else if (state.status === 'error') {
    details.push('The replay video node reports error status in the ReactFlow snapshot.')
  } else if (!state.hasOutVideo) {
    details.push('The ReactFlow snapshot has no out_video for replay_video_1, so the card is blocked waiting for generated output state.')
  } else if (state.historyCount === 0) {
    details.push('The ReactFlow snapshot has out_video, but its history array is empty.')
  } else if (!state.videoPath) {
    details.push('The ReactFlow snapshot has out_video history, but the selected entry has no result.local_path.')
  } else if (!nodeDom.videoPresent) {
    details.push(`The ReactFlow snapshot has videoPath ${state.videoPath}, but no generated video element rendered inside replay_video_1.`)
  } else if (target === 'src' && !nodeDom.videoSrc) {
    details.push('A generated video element rendered inside replay_video_1, but currentSrc/src is empty.')
  }

  if (documentDom.videoSrc && !state.videoPath) {
    details.push('The workspace DOM has a video src while the ReactFlow snapshot has no selected videoPath; this points to stale canvasNodes state or media rendered outside the node scope.')
  } else if (documentDom.videoPresent && !nodeDom.videoPresent) {
    details.push('A video element exists elsewhere in the workspace DOM, but not inside the replay_video_1 DOM scope that this card validates.')
  }

  details.push('The replay card validates ReactFlow canvasNodes plus the video node DOM; VideoGeneratorNode renders from typed YDoc out_video.history[picked_index].result.local_path.')
  return details
}

function elapsedLabel(ms: number): string {
  return `${Math.floor(ms)}ms`
}

export function classifyGeneratedVideoDomFailure(
  state: ReplayVideoNodeState,
  target: ReplayVideoDomTarget,
  timing: ReplayVideoDomFailureTiming,
): ReplayVideoDomFailure | null {
  const stateDetails = [
    `reactflow_snapshot: ${describeReplayVideoNodeState(state)}`,
    'VideoGeneratorNode renders from typed YDoc out_video.history[picked_index].result.local_path.',
  ]

  if (state.status === 'error') {
    return {
      message: 'Generated video node failed',
      observed: state.error ?? 'video node status is error',
      details: stateDetails,
    }
  }

  if (
    state.status === 'completed' &&
    !state.videoPath &&
    timing.terminalMissingOutputElapsedMs >= MEDIA_DOM_RENDER_GRACE_MS
  ) {
    return {
      message: 'Video node completed without generated video path',
      observed: describeReplayVideoNodeState(state),
      details: stateDetails,
    }
  }

  if (state.videoPath && timing.mediaReadyElapsedMs >= MEDIA_DOM_RENDER_GRACE_MS) {
    const missingTarget = target === 'element' ? 'DOM video element' : 'DOM video src'
    return {
      message: target === 'element'
        ? 'Generated video data did not render a video element'
        : 'Generated video element did not receive a src',
      observed: `out_video path ${state.videoPath} exists but ${missingTarget} missing after ${elapsedLabel(timing.mediaReadyElapsedMs)}`,
      details: stateDetails,
    }
  }

  return null
}

function updateReplayVideoStateTimers(
  state: ReplayVideoNodeState,
  timers: ReplayVideoStateTimers,
): ReplayVideoDomFailureTiming {
  const now = performance.now()
  if (state.videoPath) {
    timers.mediaReadyAt ??= now
  } else {
    timers.mediaReadyAt = null
  }

  if (state.status === 'completed' && !state.videoPath) {
    timers.terminalMissingOutputAt ??= now
  } else {
    timers.terminalMissingOutputAt = null
  }

  return {
    mediaReadyElapsedMs: timers.mediaReadyAt === null ? 0 : now - timers.mediaReadyAt,
    terminalMissingOutputElapsedMs: timers.terminalMissingOutputAt === null ? 0 : now - timers.terminalMissingOutputAt,
  }
}

function throwReplayVideoDomFailureIfNeeded(
  state: ReplayVideoNodeState,
  target: ReplayVideoDomTarget,
  timing: ReplayVideoDomFailureTiming,
  nodeDom: ReplayVideoDomSnapshot,
  documentDom: ReplayVideoDomSnapshot,
): void {
  const failure = classifyGeneratedVideoDomFailure(state, target, timing)
  if (!failure) return
  throw new ReplayCheckError(
    'failed',
    failure.message,
    failure.observed,
    buildReplayVideoBlockedDetails(state, nodeDom, documentDom, target),
  )
}

async function waitForGeneratedReplayVideoElement(
  context: ReplayRunnerContext,
  videoNode: HTMLElement,
): Promise<HTMLVideoElement> {
  let lastVideo: HTMLVideoElement | null = null
  let lastState = readReplayVideoNodeState(context)
  let lastNodeDom = readReplayVideoDomSnapshot(videoNode, 'node')
  let lastDocumentDom = readReplayVideoDomSnapshot(document, 'document')
  const timers: ReplayVideoStateTimers = { mediaReadyAt: null, terminalMissingOutputAt: null }
  const ok = await waitForCondition(() => {
    const videoMatch = queryGeneratedReplayVideoMatch(videoNode)
    const documentReplayMatch = queryGeneratedReplayVideoMatch(document, { requireReplayVideoNodeSrc: true })
    lastVideo = videoMatch?.video ?? documentReplayMatch?.video ?? null
    lastNodeDom = readReplayVideoDomSnapshot(videoNode, 'node', videoMatch)
    lastDocumentDom = readReplayVideoDomSnapshot(
      document,
      'document',
      documentReplayMatch ?? queryGeneratedReplayVideoMatch(document),
    )
    if (lastVideo) return true

    lastState = readReplayVideoNodeState(context)
    const timing = updateReplayVideoStateTimers(lastState, timers)
    throwReplayVideoDomFailureIfNeeded(lastState, 'element', timing, lastNodeDom, lastDocumentDom)
    return false
  }, remainingCheckMs(context, 45_000))

  if (!ok || !lastVideo) {
    throw new ReplayCheckError(
      'blocked',
      'Generated video element was not found',
      'generated video element missing after waiting for media state',
      buildReplayVideoBlockedDetails(lastState, lastNodeDom, lastDocumentDom, 'element'),
    )
  }
  return lastVideo
}

async function waitForGeneratedReplayVideoSrc(
  context: ReplayRunnerContext,
  reader: () => HTMLVideoElement | null,
  videoNode: HTMLElement,
): Promise<string> {
  let lastSrc = ''
  let lastState = readReplayVideoNodeState(context)
  let lastNodeDom = readReplayVideoDomSnapshot(videoNode, 'node')
  let lastDocumentDom = readReplayVideoDomSnapshot(document, 'document')
  const timers: ReplayVideoStateTimers = { mediaReadyAt: null, terminalMissingOutputAt: null }
  const ok = await waitForCondition(() => {
    const videoMatch = queryGeneratedReplayVideoMatch(videoNode)
    const documentReplayMatch = queryGeneratedReplayVideoMatch(document, { requireReplayVideoNodeSrc: true })
    const video = reader() ?? documentReplayMatch?.video ?? null
    lastSrc = videoSrcOf(video) ?? ''
    lastNodeDom = readReplayVideoDomSnapshot(videoNode, 'node', videoMatch)
    lastDocumentDom = readReplayVideoDomSnapshot(
      document,
      'document',
      documentReplayMatch ?? queryGeneratedReplayVideoMatch(document),
    )
    if (lastSrc) return true

    lastState = readReplayVideoNodeState(context)
    const timing = updateReplayVideoStateTimers(lastState, timers)
    throwReplayVideoDomFailureIfNeeded(lastState, 'src', timing, lastNodeDom, lastDocumentDom)
    return false
  }, remainingCheckMs(context, 45_000))

  if (!ok || !lastSrc) {
    throw new ReplayCheckError(
      'blocked',
      'Generated video asset did not appear',
      'video preview element exists but has no src',
      buildReplayVideoBlockedDetails(lastState, lastNodeDom, lastDocumentDom, 'src'),
    )
  }
  return lastSrc
}

function pass(observed: string, details: string[] = []): ReplayCheckResult {
  return { status: 'passed', observed, details }
}

function checkTimeoutMs(checkId: ReplayCheckId): number {
  if (checkId === 'canvas-graph-created') return CANVAS_GRAPH_TIMEOUT_MS
  if (checkId === 'canvas-tab-create-delete') return CANVAS_GRAPH_TIMEOUT_MS
  if (checkId === 'director-created-canvas-handoff') return CANVAS_GRAPH_TIMEOUT_MS
  if (checkId === 'jsonb-load-validator') return JSONB_VALIDATOR_TIMEOUT_MS
  if (checkId === 'mocked-assets-completed') return ASSET_CHECK_TIMEOUT_MS
  if (checkId === 't277192495-video-editor-timeline-track-scroll') return TIMELINE_SCROLL_CHECK_TIMEOUT_MS
  if (checkId === 'video-editor-preview-audio') return AUDIO_CHECK_TIMEOUT_MS
  return UI_CHECK_TIMEOUT_MS
}

function remainingCheckMs(context: ReplayRunnerContext, maxMs: number): number {
  if (!context.checkDeadlineMs) return maxMs
  return Math.max(1_000, Math.min(maxMs, Math.floor(context.checkDeadlineMs - performance.now())))
}

async function runCheckWithTimeout(
  check: ReplayCheckDefinition,
  context: ReplayRunnerContext,
): Promise<ReplayCheckResult> {
  let timeoutId: number | undefined
  const timeoutMs = checkTimeoutMs(check.id)
  const timedContext: ReplayRunnerContext = {
    ...context,
    checkDeadlineMs: performance.now() + timeoutMs,
  }
  const timeout = new Promise<ReplayCheckResult>((resolve) => {
    timeoutId = window.setTimeout(() => {
      resolve({
        status: 'blocked',
        observed: `${check.title} exceeded ${timeoutMs}ms`,
        details: [
          'The in-app replay check timed out before the outer Playwright test timeout.',
          'Replay this card after the canvas is idle to distinguish a slow load from a broken UI path.',
        ],
      })
    }, timeoutMs)
  })

  try {
    return await Promise.race([check.run(timedContext), timeout])
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId)
  }
}

function describeRefs(rail: HTMLElement | null): string {
  const videoRefs = rail?.querySelectorAll('[data-testid="timeline-editor-video-reference"], [data-testid="video-editor-video-reference"]').length ?? 0
  const audioRefs = rail?.querySelectorAll('[data-testid="timeline-editor-audio-reference"], [data-testid="video-editor-audio-reference"]').length ?? 0
  return `${videoRefs} video reference(s), ${audioRefs} audio reference(s)`
}

function describeTimelinePreviewState(preview: HTMLElement | null): string {
  if (!preview) return 'preview_state: preview=missing'
  const data = preview.dataset
  return [
    'preview_state: preview=present',
    `videoCount=${data.videoCount ?? 'unknown'}`,
    `audioCount=${data.audioCount ?? 'unknown'}`,
    `playing=${data.isPlaying ?? 'unknown'}`,
    `muted=${data.muted ?? 'unknown'}`,
    `playhead=${data.playheadPosition ?? 'unknown'}`,
    `totalDuration=${data.totalDuration ?? 'unknown'}`,
    `preloaderReady=${data.preloaderReady ?? 'unknown'}`,
    `preloaderLoading=${data.preloaderLoadingCount ?? 'unknown'}`,
    `preloaderErrors=${data.preloaderErrorPaths ?? 'unknown'}`,
  ].join(' ')
}

function describeAudioGainHandles(): string {
  const entries = Array.from(window.__nadAudioPoolGains?.entries() ?? [])
    .map(([clipId, gain]) => `${clipId}:${gain.gain.value}`)
  return `gain_handles: ${entries.join(', ') || 'none'}`
}

async function runCanvasGraphCreated(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  const ok = await waitForCondition(
    () =>
      missingRequiredNodes(context.getCanvasNodeIds()).length === 0 &&
      missingRequiredEdges(context.getCanvasEdges()).length === 0,
    remainingCheckMs(context, CANVAS_GRAPH_TIMEOUT_MS),
  )
  const canvasNodeIds = context.getCanvasNodeIds()
  const canvasEdges = context.getCanvasEdges()
  const missing = missingRequiredNodes(canvasNodeIds)
  const missingEdges = missingRequiredEdges(canvasEdges)
  if (!ok || missing.length > 0 || missingEdges.length > 0) {
    throw new ReplayCheckError(
      'blocked',
      'Replay graph prerequisites are missing',
      `missing node(s): ${missing.join(', ') || 'none'}; missing edge(s): ${missingEdges.join(', ') || 'none'}`,
      [
        `available nodes: ${Array.from(canvasNodeIds).sort().join(', ') || 'none'}`,
        `available edges: ${canvasEdges.map((edge) => `${edge.id}:${edge.source}->${edge.target}`).sort().join(', ') || 'none'}`,
      ],
    )
  }
  return pass(`required nodes and edges present: ${REQUIRED_NODE_IDS.join(', ')}`)
}

async function runDeletedNodeEdgeCleanup(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  const ok = await waitForCondition(() => {
    const canvasNodeIds = context.getCanvasNodeIds()
    const canvasEdges = context.getCanvasEdges()
    return (
      !canvasNodeIds.has(DELETE_PROBE_NODE_ID) &&
      missingRequiredEdges(canvasEdges).length === 0 &&
      findDanglingReplayEdges(canvasNodeIds, canvasEdges).length === 0
    )
  }, remainingCheckMs(context, UI_CHECK_TIMEOUT_MS))

  const canvasNodeIds = context.getCanvasNodeIds()
  const canvasEdges = context.getCanvasEdges()
  const danglingEdges = findDanglingReplayEdges(canvasNodeIds, canvasEdges)
  const missingEdges = missingRequiredEdges(canvasEdges)
  if (canvasNodeIds.has(DELETE_PROBE_NODE_ID)) {
    throw new ReplayCheckError(
      'blocked',
      'Replay deletion probe node is still present',
      `${DELETE_PROBE_NODE_ID} still exists after ${UI_CHECK_TIMEOUT_MS}ms`,
      [`available nodes: ${Array.from(canvasNodeIds).sort().join(', ') || 'none'}`],
    )
  }
  if (danglingEdges.length > 0) {
    throw new ReplayCheckError(
      'failed',
      'Replay graph has dangling edge endpoints',
      describeDanglingEdges(danglingEdges),
      [`edge count: ${canvasEdges.length}`],
    )
  }
  if (!ok || missingEdges.length > 0) {
    throw new ReplayCheckError(
      'blocked',
      'Replay graph required edges are missing after cleanup',
      `missing edge(s): ${missingEdges.join(', ') || 'not reported'}`,
      [`available edges: ${canvasEdges.map((edge) => `${edge.id}:${edge.source}->${edge.target}`).sort().join(', ') || 'none'}`],
    )
  }

  return pass(`removed ${DELETE_PROBE_NODE_ID}; ${canvasEdges.length} edge(s) have valid endpoints`)
}

async function runCanvasTabCreateDelete(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  if (!context.onCreateCanvas || !context.onDeleteCanvas) {
    throw new ReplayCheckError(
      'blocked',
      'Replay canvas operations are unavailable',
      `create=${String(Boolean(context.onCreateCanvas))}, delete=${String(Boolean(context.onDeleteCanvas))}`,
    )
  }

  await waitForElement(
    'canvas tabs',
    () => queryHtml('[data-testid="canvas-tabs"]'),
    remainingCheckMs(context, 10_000),
  )
  const beforeList = await readReplayCanvasList(context.projectId)
  const createdCanvas = await context.onCreateCanvas(CANVAS_TAB_OPERATION_TITLE)

  const createdTab = await waitForElement(
    'created replay canvas tab',
    () => findCanvasTabByTitle(CANVAS_TAB_OPERATION_TITLE),
    remainingCheckMs(context, 15_000),
  )
  const createdCanvasId = canvasIdFromTabTestId(createdTab.getAttribute('data-testid'))
  if (!createdCanvasId || createdCanvasId !== createdCanvas.id) {
    throw new ReplayCheckError(
      'failed',
      'Created canvas tab did not expose a canvas id test id',
      `expected=${createdCanvas.id}; testId=${createdTab.getAttribute('data-testid') ?? 'missing data-testid'}`,
      [`tabs: ${describeCanvasTabs()}`],
    )
  }

  const createdPersisted = await waitForCondition(async () => {
    const list = await readReplayCanvasList(context.projectId)
    return list.canvases.some((canvas) => canvas.id === createdCanvasId && canvas.title === CANVAS_TAB_OPERATION_TITLE)
  }, remainingCheckMs(context, 10_000))
  if (!createdPersisted) {
    const list = await readReplayCanvasList(context.projectId)
    throw new ReplayCheckError(
      'blocked',
      'Created replay canvas did not appear in the backend canvas list',
      `created=${createdCanvasId}; backend=${list.canvases.map((canvas) => `${canvas.id}:${canvas.title}`).join(', ') || 'none'}`,
      [`tabs: ${describeCanvasTabs()}`],
    )
  }

  await context.onDeleteCanvas(createdCanvasId)

  const deleted = await waitForCondition(async () => {
    const list = await readReplayCanvasList(context.projectId)
    return (
      findCanvasTabByTitle(CANVAS_TAB_OPERATION_TITLE) === null &&
      !list.canvases.some((canvas) => canvas.id === createdCanvasId)
    )
  }, remainingCheckMs(context, 15_000))

  if (!deleted) {
    const list = await readReplayCanvasList(context.projectId)
    throw new ReplayCheckError(
      'failed',
      'Temporary replay canvas was not deleted from UI and backend state',
      `created=${createdCanvasId}; backend=${list.canvases.map((canvas) => `${canvas.id}:${canvas.title}`).join(', ') || 'none'}`,
      [`tabs: ${describeCanvasTabs()}`],
    )
  }

  return pass(`created and deleted canvas ${createdCanvasId}`, [
    `backend canvas count: ${beforeList.canvases.length} -> ${beforeList.canvases.length + 1} -> ${beforeList.canvases.length}`,
    `tabs after delete: ${describeCanvasTabs()}`,
  ])
}

async function runDirectorCreatedCanvasHandoff(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  await waitForElement(
    'Director-created canvas tab',
    () => queryButton(`[data-testid="canvas-tab-${DIRECTOR_CREATED_CANVAS_ID}"]`),
    remainingCheckMs(context, 20_000),
  )

  const persisted = await waitForCondition(async () => {
    const list = await readReplayCanvasList(context.projectId)
    return list.canvases.some(
      (canvas) =>
        canvas.id === DIRECTOR_CREATED_CANVAS_ID &&
        canvas.title === DIRECTOR_CREATED_CANVAS_TITLE,
    )
  }, remainingCheckMs(context, 15_000))

  if (!persisted) {
    const list = await readReplayCanvasList(context.projectId)
    throw new ReplayCheckError(
      'blocked',
      'Director-created replay canvas did not appear in the backend canvas list',
      `expected=${DIRECTOR_CREATED_CANVAS_ID}:${DIRECTOR_CREATED_CANVAS_TITLE}; backend=${list.canvases.map((canvas) => `${canvas.id}:${canvas.title}`).join(', ') || 'none'}`,
      [`tabs: ${describeCanvasTabs()}`],
    )
  }

  const missingSourceNodes = [OBJECT_NODE_ID, LOCATION_NODE_ID].filter(
    (nodeId) => !context.getCanvasNodeIds().has(nodeId),
  )
  if (missingSourceNodes.length > 0) {
    throw new ReplayCheckError(
      'blocked',
      'Replay source nodes were missing before validating the copied canvas',
      `missing source node(s): ${missingSourceNodes.join(', ')}`,
      [`available nodes: ${Array.from(context.getCanvasNodeIds()).sort().join(', ') || 'none'}`],
    )
  }

  return pass(`Director-created canvas ${DIRECTOR_CREATED_CANVAS_ID} is visible and persisted`, [
    `tabs: ${describeCanvasTabs()}`,
    `source nodes copied from: ${OBJECT_NODE_ID}, ${LOCATION_NODE_ID}`,
  ])
}

type JsonbValidatorResponse = {
  ok?: boolean
  validated?: boolean
  revision?: number
  schema_version?: number
  node_count?: number
  edge_count?: number
  snapshot_bytes?: number
  detail?: JsonbValidatorDetail
}

type JsonbValidatorDetail =
  | string
  | number
  | boolean
  | null
  | JsonbValidatorDetail[]
  | { [key: string]: JsonbValidatorDetail }

function validatorDetail(payload: JsonbValidatorResponse | null, fallback: string): string {
  if (!payload) return fallback
  if (typeof payload.detail === 'string') return payload.detail
  if (payload.detail !== undefined) return JSON.stringify(payload.detail)
  return fallback
}

async function readJsonbValidatorResponse(response: Response): Promise<JsonbValidatorResponse | null> {
  const payload: JsonbValidatorResponse | null = await response.json().catch(() => null)
  return payload
}

async function runJsonbLoadValidator(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  let lastObserved = 'JSONB validator has not returned yet'
  const path = `/projects/${context.projectId}/agentic/replay/jsonb-validator`
  const ok = await waitForCondition(async () => {
    const response = await apiFetch(path, { method: 'GET' }).catch((error: Error) => {
      lastObserved = error.message
      return null
    })
    if (response === null) return false
    const payload = await readJsonbValidatorResponse(response)
    if (response.ok && payload?.validated === true) {
      lastObserved = `revision ${payload.revision ?? 'unknown'}, ${payload.node_count ?? '?'} node(s), ${payload.edge_count ?? '?'} edge(s)`
      return true
    }
    const detail = validatorDetail(payload, response.statusText)
    lastObserved = `${response.status} ${detail}`
    if (response.status >= 500) {
      throw new ReplayCheckError(
        'failed',
        'Backend JSONB load validator failed',
        lastObserved,
        ['This is the persisted JSONB materialization path that catches dangling edge endpoints.'],
      )
    }
    return false
  }, remainingCheckMs(context, JSONB_VALIDATOR_TIMEOUT_MS))

  if (!ok) {
    throw new ReplayCheckError(
      'blocked',
      'Backend JSONB load validator did not pass',
      lastObserved,
      ['The replay API must persist JSONB rows before this check can validate them.'],
    )
  }
  return pass(lastObserved)
}

async function runMockedAssetsCompleted(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  await focusReplayNode(context, VIDEO_NODE_ID)
  const videoNode = await waitForElement('generated video node', () => nodeElement(VIDEO_NODE_ID), remainingCheckMs(context, 15_000))
  const video = await waitForGeneratedReplayVideoElement(context, videoNode)
  const videoSrc = await waitForGeneratedReplayVideoSrc(
    context,
    () =>
      queryGeneratedReplayVideoElement(videoNode) ??
      queryGeneratedReplayVideoElement(document, { requireReplayVideoNodeSrc: true }) ??
      video,
    videoNode,
  )

  await focusReplayNode(context, EDITOR_NODE_ID)
  const rail = await openReplayTimelineMediaRail(context)
  const refsReady = await waitForCondition(
    () =>
      rail.querySelectorAll('[data-testid="timeline-editor-video-reference"]').length > 0 &&
      rail.querySelectorAll('[data-testid="timeline-editor-audio-reference"]').length > 0,
    remainingCheckMs(context, 45_000),
  )
  if (!refsReady) {
    throw new ReplayCheckError(
      'blocked',
      'Mocked media references did not appear in the editor',
      describeRefs(rail),
      [`video src: ${videoSrc}`],
    )
  }

  const observedRefs = describeRefs(rail)
  await closeReplayTimelineEditor()

  return pass('video preview src exists and editor has video/audio references', [
    `video src: ${videoSrc}`,
    `editor refs: ${observedRefs}`,
  ])
}

async function runProjectLeavesDraft(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  let lastStatus = 'unknown'
  const deadline = performance.now() + remainingCheckMs(context, 45_000)
  while (performance.now() < deadline) {
    const project = await readJson<{ status?: string | null }>(`/projects/${context.projectId}/full`)
    lastStatus = typeof project.status === 'string' ? project.status : 'missing'
    if (lastStatus.toLowerCase() !== 'draft') {
      return pass(`project.status=${lastStatus}`)
    }
    await delay(1_000)
  }
  throw new ReplayCheckError(
    'failed',
    'Project stayed in Draft after mocked video generation completed',
    `project.status=${lastStatus}`,
  )
}

async function runObjectLocationGridLayout(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  const objectBox = await measureFlowBox(context, OBJECT_NODE_ID)
  const locationBox = await measureFlowBox(context, LOCATION_NODE_ID)
  const overlaps = flowBoxesOverlap(objectBox, locationBox)
  const verticalGap = locationBox.y - (objectBox.y + objectBox.height)

  if (overlaps) {
    throw new ReplayCheckError(
      'failed',
      'Object Designer and Location Designer rows overlap on the replay canvas',
      `object bottom=${(objectBox.y + objectBox.height).toFixed(1)}, location top=${locationBox.y.toFixed(1)}, gap=${verticalGap.toFixed(1)}`,
      [
        `${OBJECT_NODE_ID}: x=${objectBox.x.toFixed(1)} y=${objectBox.y.toFixed(1)} w=${objectBox.width} h=${objectBox.height}`,
        `${LOCATION_NODE_ID}: x=${locationBox.x.toFixed(1)} y=${locationBox.y.toFixed(1)} w=${locationBox.width} h=${locationBox.height}`,
      ],
    )
  }

  return pass(
    `no rectangle overlap; vertical gap=${verticalGap.toFixed(1)}px`,
    [
      `${OBJECT_NODE_ID}: x=${objectBox.x.toFixed(1)} y=${objectBox.y.toFixed(1)} w=${objectBox.width} h=${objectBox.height}`,
      `${LOCATION_NODE_ID}: x=${locationBox.x.toFixed(1)} y=${locationBox.y.toFixed(1)} w=${locationBox.width} h=${locationBox.height}`,
    ],
  )
}

async function runSettingsMyVideoModel(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  const modal = await openProjectSettings(context)
  try {
    const hasSection = containsText(modal, 'My Video Model')
    const addButton = queryButton('[data-testid="add-gateway-button"]', modal)
    if (!hasSection || !addButton) {
      throw new ReplayCheckError(
        'failed',
        'Project Settings did not expose the My Video Model section',
        `section=${String(hasSection)}, addButton=${String(Boolean(addButton))}`,
      )
    }
    return pass('Project Settings contains My Video Model and Add Model')
  } finally {
    await closeProjectSettings()
  }
}

async function runByomEditKeyState(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  const config = await ensureReplayGatewayConfig()
  const modal = await openProjectSettings(context)
  try {
    const row = await waitForElement(
      'Replay BYOM config row',
      () => queryHtml(`[data-testid="gateway-config-${config.id}"]`, modal),
      remainingCheckMs(context, 10_000),
    )
    const edit = await waitForElement(
      'Replay BYOM edit button',
      () => queryButton(`[data-testid="edit-config-${config.id}"]`, row),
      remainingCheckMs(context, 5_000),
    )
    edit.click()
    const currentKey = await waitForElement(
      'Current App Key field',
      () => queryInput('[data-testid="gateway-modal-current-key"]'),
      remainingCheckMs(context, 10_000),
    )
    const newKey = await waitForElement(
      'New App Key field',
      () => queryInput('[data-testid="gateway-modal-api-key"]'),
      remainingCheckMs(context, 5_000),
    )

    if (!currentKey.value.trim()) {
      throw new ReplayCheckError(
        'failed',
        'Edit Model modal showed a blank Current App Key',
        'gateway-modal-current-key value is empty',
      )
    }
    if (newKey.value !== '') {
      throw new ReplayCheckError(
        'failed',
        'Edit Model modal prefilled the replacement key field',
        `gateway-modal-api-key value length=${newKey.value.length}`,
      )
    }

    return pass('Current App Key is masked and New App Key is blank', [
      `config id: ${config.id}`,
      `current key display: ${currentKey.value}`,
    ])
  } finally {
    queryButton('[data-testid="gateway-modal-cancel"]')?.click()
    await waitForElementGone(
      'gateway edit modal',
      () => queryHtml('[data-testid="gateway-modal-api-key"]'),
      5_000,
    ).catch(() => undefined)
    await closeProjectSettings()
  }
}

async function runVideoConfigMyModelChip(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  const config = await ensureReplayGatewayConfig()
  const modelResponse = await getVideoModels()
  const byomModelId = `gateway/byom/${config.id}`
  const byomModel = modelResponse.byom.find((model) => model.id === byomModelId)
  if (!byomModel) {
    throw new ReplayCheckError(
      'blocked',
      'Replay BYOM model did not appear in /video-models',
      `expected ${byomModelId}; got ${modelResponse.byom.map((model) => model.id).join(', ') || 'none'}`,
    )
  }

  const panel = await openVideoConfigPanel(context)
  const pill = await waitForElement(
    'Video Config pill',
    () => queryButton('[data-testid="video-config-pill"]', panel),
    remainingCheckMs(context, 10_000),
  )
  pill.click()
  const flyout = await waitForElement(
    'Video Config flyout',
    () => queryHtml('[data-testid="video-config-flyout-content"]'),
    remainingCheckMs(context, 10_000),
  )
  const chips = await waitForElement(
    'My Video Model chips',
    () => queryHtml('[data-testid="byom-model-chips"]', flyout),
    remainingCheckMs(context, 10_000),
  )
  const chip = queryButton(`[data-testid="model-byom-${byomModelId}"]`, chips)
  const star = queryElement(`[data-testid="model-byom-default-star-${byomModelId}"]`, chips)
  if (!chip || !star || !containsText(flyout, 'My Video Model')) {
    throw new ReplayCheckError(
      'failed',
      'Video Config flyout did not show the default My Video Model chip',
      `chip=${String(Boolean(chip))}, star=${String(Boolean(star))}, section=${String(containsText(flyout, 'My Video Model'))}`,
      [`byom response default=${String(byomModel.is_default)} enabled=${String(byomModel.enabled)}`],
    )
  }

  pill.click()
  await waitForElementGone(
    'Video Config flyout',
    () => queryHtml('[data-testid="video-config-flyout-content"]'),
    3_000,
  ).catch(() => undefined)

  return pass('Video Config shows My Video Model chip with default star', [
    `chip text: ${elementText(chip)}`,
    `model id: ${byomModelId}`,
  ])
}

async function runGeneratedVideoNodePlayback(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  await focusReplayNode(context, VIDEO_NODE_ID)
  const videoNode = await waitForElement('generated video node', () => nodeElement(VIDEO_NODE_ID), remainingCheckMs(context, 15_000))
  const player = await waitForElement(
    'generated video player',
    () => queryHtml('[data-testid="video-preview-player"]', videoNode),
    remainingCheckMs(context, 15_000),
  )
  const video = await waitForElement(
    'generated video element',
    () => queryVideo('video', player),
    remainingCheckMs(context, 15_000),
  )
  const playButton = await waitForElement(
    'generated video play button',
    () => queryButton('[data-testid="video-play-pause-btn"]', videoNode),
    remainingCheckMs(context, 15_000),
  )

  if (titleOf(playButton) === 'Pause') {
    playButton.click()
    const reset = await waitForCondition(() => titleOf(playButton) === 'Play', remainingCheckMs(context, 5_000))
    if (!reset) {
      throw new ReplayCheckError(
        'failed',
        'Generated video play button could not reset before replay',
        `button title: ${titleOf(playButton)}`,
      )
    }
  }

  let sawPlayEvent = false
  const markPlay = () => { sawPlayEvent = true }
  video.addEventListener('play', markPlay, { once: true })
  playButton.click()

  const started = await waitForCondition(
    () => sawPlayEvent || titleOf(playButton) === 'Pause' || !video.paused,
    remainingCheckMs(context, 5_000),
  )
  video.removeEventListener('play', markPlay)
  if (!started) {
    throw new ReplayCheckError(
      'failed',
      'Generated video node did not enter playback after clicking Play',
      `button title: ${titleOf(playButton)}, paused: ${String(video.paused)}`,
      [`video src: ${video.currentSrc || video.src}`],
    )
  }

  return pass(`button title: ${titleOf(playButton)}, paused: ${String(video.paused)}`, [
    `video src: ${video.currentSrc || video.src}`,
    `play event observed: ${String(sawPlayEvent)}`,
  ])
}

async function runVideoEditorReferences(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  await focusReplayNode(context, EDITOR_NODE_ID)
  const rail = await openReplayTimelineMediaRail(context)
  const videoRefs = rail.querySelectorAll('[data-testid="timeline-editor-video-reference"]').length
  const audioRefs = rail.querySelectorAll('[data-testid="timeline-editor-audio-reference"]').length
  if (videoRefs < 1 || audioRefs < 1) {
    throw new ReplayCheckError(
      'blocked',
      'Video Editor is missing required references',
      describeRefs(rail),
    )
  }
  const observedRefs = describeRefs(rail)
  await closeReplayTimelineEditor()
  return pass(observedRefs)
}

async function runVideoConfigHiddenWhileGenerating(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  await openVideoConfigPanel(context)
  const originalStatus = window.__dwReadNodeField?.(VIDEO_NODE_ID, 'status')
  setYDocNodeField(VIDEO_NODE_ID, 'status', 'running')
  try {
    const hidden = await waitForCondition(
      () => queryHtml('[data-testid="unified-bottom-panel"]', nodeElement(VIDEO_NODE_ID) ?? document) === null,
      remainingCheckMs(context, 10_000),
    )
    if (!hidden) {
      throw new ReplayCheckError(
        'failed',
        'Editable Video Config panel stayed visible while the node status was running',
        `status=${String(window.__dwReadNodeField?.(VIDEO_NODE_ID, 'status'))}`,
      )
    }

    const videoNode = await waitForElement(
      'generated video node',
      () => nodeElement(VIDEO_NODE_ID),
      remainingCheckMs(context, 5_000),
    )
    queryButton('[data-testid="quick-info-config"]', videoNode)?.click()
    await delay(300)
    const reopened = queryHtml('[data-testid="unified-bottom-panel"]', videoNode)
    if (reopened) {
      throw new ReplayCheckError(
        'failed',
        'QuickInfo Config reopened the editable panel while the video node was running',
        `panel text=${elementText(reopened).slice(0, 200)}`,
      )
    }

    return pass('Video Config panel hides while running and QuickInfo Config cannot reopen it')
  } finally {
    setYDocNodeField(
      VIDEO_NODE_ID,
      'status',
      typeof originalStatus === 'string' ? originalStatus : 'completed',
    )
    await delay(500)
  }
}

type TimelineTrackMeasurement = {
  key: string
  dTop: number
  dHeight: number
}

function measureTimelineTrackAlignment(): TimelineTrackMeasurement[] {
  const headers = Array.from(
    document.querySelectorAll<HTMLElement>(
      '[data-testid^="timeline-video-track-header-"], [data-testid^="timeline-audio-track-header-"]',
    ),
  )
  const out: TimelineTrackMeasurement[] = []
  for (const header of headers) {
    const headerTestId = header.getAttribute('data-testid') ?? ''
    const rowTestId = headerTestId.replace('-track-header-', '-track-')
    const row = queryHtml(`[data-testid="${rowTestId}"]`)
    if (!row) continue
    const headerRect = header.getBoundingClientRect()
    const rowRect = row.getBoundingClientRect()
    out.push({
      key: headerTestId,
      dTop: Math.abs(headerRect.top - rowRect.top),
      dHeight: Math.abs(headerRect.height - rowRect.height),
    })
  }
  return out
}

function assertTimelineTrackAlignment(phase: string, measurements: TimelineTrackMeasurement[]): void {
  if (measurements.length < 4) {
    throw new ReplayCheckError(
      'blocked',
      'Timeline did not render enough track rows to verify scroll alignment',
      `${phase}: measured ${measurements.length} header/row pair(s)`,
    )
  }

  const misaligned = measurements.filter((pair) => pair.dTop > 3 || pair.dHeight > 3)
  if (misaligned.length > 0) {
    throw new ReplayCheckError(
      'failed',
      'Timeline track headers drifted away from their rows while scrolling',
      `${phase}: ${misaligned
        .map((pair) => `${pair.key} dTop=${pair.dTop.toFixed(1)} dHeight=${pair.dHeight.toFixed(1)}`)
        .join('; ')}`,
    )
  }
}

function assertTimelineLaneCoverage(phase: string): void {
  const body = queryHtml('[data-testid="timeline-scroll-body"]')
  const content = queryHtml('[data-testid="timeline-scroll-content"]')
  if (!body || !content) {
    throw new ReplayCheckError(
      'blocked',
      'Timeline scroll body/content did not render',
      `${phase}: body=${String(Boolean(body))}, content=${String(Boolean(content))}`,
    )
  }

  const bodyRect = body.getBoundingClientRect()
  const rows = Array.from(
    document.querySelectorAll<HTMLElement>(
      '[data-testid^="timeline-video-track-"], [data-testid^="timeline-audio-track-"]',
    ),
  ).filter((row) => /^timeline-(video|audio)-track-\d+$/.test(row.getAttribute('data-testid') ?? ''))
  if (rows.length < 4) {
    throw new ReplayCheckError(
      'blocked',
      'Timeline did not render enough scrollable track lane surfaces',
      `${phase}: lane rows=${rows.length}`,
    )
  }

  const uncovered = rows.filter((row) => {
    const rect = row.getBoundingClientRect()
    const bg = window.getComputedStyle(row).backgroundColor
    return rect.width + 1 < bodyRect.width || rect.height < 40 || bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent'
  })
  if (uncovered.length > 0) {
    throw new ReplayCheckError(
      'failed',
      'Timeline has track labels without a full scrollable lane surface',
      `${phase}: ${uncovered
        .map((row) => {
          const rect = row.getBoundingClientRect()
          return `${row.getAttribute('data-testid')} width=${rect.width.toFixed(1)} height=${rect.height.toFixed(1)} bg=${window.getComputedStyle(row).backgroundColor}`
        })
        .join('; ')}`,
    )
  }
}

async function addTimelineTrack(
  context: ReplayRunnerContext,
  kind: 'video' | 'audio',
): Promise<number> {
  const headerPrefix = `timeline-${kind}-track-header-`
  const before = document.querySelectorAll(`[data-testid^="${headerPrefix}"]`).length
  const addButton = await waitForElement(
    'timeline add-track button',
    () => queryButton('[data-testid="timeline-add-track-button"]'),
    remainingCheckMs(context, 10_000),
  )
  addButton.click()
  const menuItem = await waitForElement(
    `timeline add ${kind} track menu item`,
    () => queryButton(`[data-testid="timeline-add-${kind}-track"]`),
    remainingCheckMs(context, 5_000),
  )
  menuItem.click()
  const added = await waitForCondition(
    () => document.querySelectorAll(`[data-testid^="${headerPrefix}"]`).length > before,
    remainingCheckMs(context, 5_000),
  )
  if (!added) {
    throw new ReplayCheckError(
      'failed',
      `Timeline did not add a ${kind} track`,
      `${kind} track header count stayed at ${before}`,
    )
  }
  return before + 1
}

async function openReplayTimelineEditor(context: ReplayRunnerContext): Promise<HTMLElement> {
  await focusReplayNode(context, EDITOR_NODE_ID)
  const existingModal = queryHtml('[data-testid="timeline-modal-shell"]')
  if (existingModal) return existingModal

  const editorNode = await waitForElement(
    'video editor node',
    () => nodeElement(EDITOR_NODE_ID),
    remainingCheckMs(context, 15_000),
  )
  const openButton = await waitForElement(
    'video editor Open Editor button',
    () =>
      queryButton(
        '[data-testid="video-editor-open-editor"], [data-testid="video-editor-open-editor-main"], [data-testid="video-editor-open-timeline"]',
        editorNode,
      ),
    remainingCheckMs(context, 15_000),
  )
  openButton.click()
  return waitForElement(
    'timeline editor modal',
    () => queryHtml('[data-testid="timeline-modal-shell"]'),
    remainingCheckMs(context, 15_000),
  )
}

async function openReplayTimelineMediaRail(context: ReplayRunnerContext): Promise<HTMLElement> {
  const modal = await openReplayTimelineEditor(context)
  const mediaTab = queryButton('[data-testid="timeline-media-tab"]', modal)
  mediaTab?.click()
  return waitForElement(
    'timeline editor reference rail',
    () => queryHtml('[data-testid="timeline-editor-reference-rail"]', modal),
    remainingCheckMs(context, 15_000),
  )
}

async function closeReplayTimelineEditor(): Promise<void> {
  const closeButton = queryButton('[data-testid="timeline-modal-close-button"]')
  closeButton?.click()
  await waitForCondition(
    () => queryHtml('[data-testid="timeline-modal-shell"]') === null,
    5_000,
  )
}

async function runVideoEditorTimelineTrackScroll(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  const modal = await openReplayTimelineEditor(context)

  try {
    await waitForElement(
      'timeline scroll wrapper',
      () => queryHtml('[data-testid="timeline-scroll-wrapper"]'),
      remainingCheckMs(context, 15_000),
    )
    await waitForElement(
      'timeline scroll body',
      () => queryHtml('[data-testid="timeline-scroll-body"]'),
      remainingCheckMs(context, 15_000),
    )

    for (let i = 0; i < 2; i += 1) {
      await addTimelineTrack(context, 'video')
    }
    for (let i = 0; i < 6; i += 1) {
      await addTimelineTrack(context, 'audio')
    }

    await delay(200)
    const before = measureTimelineTrackAlignment()
    assertTimelineTrackAlignment('before wheel', before)
    assertTimelineLaneCoverage('before wheel')

    const wrapper = await waitForElement(
      'timeline scroll wrapper',
      () => queryHtml('[data-testid="timeline-scroll-wrapper"]'),
      remainingCheckMs(context, 5_000),
    )
    const body = await waitForElement(
      'timeline scroll body',
      () => queryHtml('[data-testid="timeline-scroll-body"]'),
      remainingCheckMs(context, 5_000),
    )
    const beforeTop = wrapper.scrollTop
    const maxTop = wrapper.scrollHeight - wrapper.clientHeight
    if (maxTop <= 0) {
      throw new ReplayCheckError(
        'blocked',
        'Timeline did not overflow vertically after adding tracks',
        `scrollHeight=${wrapper.scrollHeight}, clientHeight=${wrapper.clientHeight}`,
      )
    }

    body.dispatchEvent(new WheelEvent('wheel', {
      bubbles: true,
      cancelable: false,
      deltaY: Math.min(220, maxTop),
      deltaX: 0,
      view: window,
    }))
    await delay(150)

    if (wrapper.scrollTop <= beforeTop) {
      throw new ReplayCheckError(
        'failed',
        'Wheel over the timeline body did not scroll the shared track wrapper',
        `before=${beforeTop}, after=${wrapper.scrollTop}, max=${maxTop}, bodyScrollTop=${body.scrollTop}`,
      )
    }
    if (body.scrollTop !== 0) {
      throw new ReplayCheckError(
        'failed',
        'Timeline body became its own vertical scroll container',
        `wrapperScrollTop=${wrapper.scrollTop}, bodyScrollTop=${body.scrollTop}`,
      )
    }

    const after = measureTimelineTrackAlignment()
    assertTimelineTrackAlignment('after wheel', after)
    assertTimelineLaneCoverage('after wheel')

    const saveControls = queryHtml('[data-testid="timeline-modal-save-controls"]')
    const addTrackButton = queryHtml('[data-testid="timeline-add-track-button"]')
    if (!isVisibleElement(saveControls) || !isVisibleElement(addTrackButton) || !isVisibleElement(modal)) {
      throw new ReplayCheckError(
        'failed',
        'Timeline controls were not visible after adding tracks and scrolling',
        `saveControls=${String(isVisibleElement(saveControls))}, addTrack=${String(isVisibleElement(addTrackButton))}, modal=${String(isVisibleElement(modal))}`,
      )
    }

    const trackCount = document.querySelectorAll(
      '[data-testid^="timeline-video-track-header-"], [data-testid^="timeline-audio-track-header-"]',
    ).length
    return pass(
      `wheel moved wrapper ${beforeTop}→${wrapper.scrollTop}; ${trackCount} tracks aligned`,
      [
        `max vertical scroll: ${maxTop}`,
        `measured pairs: ${after.length}`,
        `body scrollTop: ${body.scrollTop}`,
      ],
    )
  } finally {
    await closeReplayTimelineEditor()
  }
}

async function runVideoEditorPreviewAudio(context: ReplayRunnerContext): Promise<ReplayCheckResult> {
  window.__NAD_DEBUG__ = true
  await focusReplayNode(context, EDITOR_NODE_ID)
  const editorNode = await waitForElement(
    'video editor node',
    () => nodeElement(EDITOR_NODE_ID),
    remainingCheckMs(context, 15_000),
  )
  await waitForElement(
    'video editor preview surface',
    () => queryHtml('[data-testid="timeline-preview-player"]', editorNode),
    remainingCheckMs(context, 15_000),
  )
  const muteButton = await waitForElement(
    'video editor mute button',
    () => queryButton('[data-testid="video-editor-preview-mute"]', editorNode),
    remainingCheckMs(context, 15_000),
  )
  const playButton = await waitForElement(
    'video editor play button',
    () => queryButton('[data-testid="video-editor-preview-play"]', editorNode),
    remainingCheckMs(context, 15_000),
  )

  if (titleOf(muteButton) === 'Unmute') {
    muteButton.click()
    const unmuted = await waitForCondition(
      () => titleOf(muteButton) === 'Mute',
      remainingCheckMs(context, 5_000),
    )
    if (!unmuted) {
      throw new ReplayCheckError(
        'failed',
        'Video Editor preview did not unmute',
        `mute button title: ${titleOf(muteButton)}`,
      )
    }
  }

  if (titleOf(playButton) === 'Pause') {
    playButton.click()
    await waitForCondition(() => titleOf(playButton) === 'Play', remainingCheckMs(context, 3_000))
  }

  playButton.click()
  const playing = await waitForCondition(
    () => titleOf(playButton) === 'Pause',
    remainingCheckMs(context, 5_000),
  )
  if (!playing) {
    throw new ReplayCheckError(
      'failed',
      'Video Editor preview did not enter playback',
      `play button title: ${titleOf(playButton)}`,
    )
  }

  const hasAudibleAudio = await waitForCondition(() => {
    const gains = window.__nadAudioPoolGains
    const hasGain = gains ? Array.from(gains.values()).some((gain) => gain.gain.value > 0) : false
    const elements = window.__nadAudioPoolElements
    const hasNativeElement = elements
      ? Array.from(elements.values()).some((element) => !element.muted && element.volume > 0)
      : false
    return hasGain || hasNativeElement
  }, remainingCheckMs(context, 10_000))
  const gainValues = Array.from(window.__nadAudioPoolGains?.values() ?? [])
    .map((gain) => gain.gain.value)
  const nativeStates = Array.from(window.__nadAudioPoolElements?.entries() ?? [])
    .map(([clipId, element]) => (
      `${clipId}:muted=${String(element.muted)} volume=${element.volume.toFixed(2)} paused=${String(element.paused)}`
    ))
  const preview = queryHtml('[data-testid="timeline-preview-player"]', editorNode)
  if (!hasAudibleAudio) {
    throw new ReplayCheckError(
      'failed',
      'Video Editor preview did not expose audible audio output',
      `play button title: ${titleOf(playButton)}, mute button title: ${titleOf(muteButton)}, gains: ${gainValues.join(', ') || 'none'}, native: ${nativeStates.join(', ') || 'none'}`,
      [
        describeTimelinePreviewState(preview),
        describeAudioGainHandles(),
        `native audio: ${nativeStates.join(', ') || 'none'}`,
        'Expected either WebAudio gain > 0 or a native pooled audio element with muted=false and volume > 0.',
        'If the browser blocks autoplay audio, click Replay This Check once after interacting with the page.',
      ],
    )
  }

  return pass(
    `play button title: ${titleOf(playButton)}, mute button title: ${titleOf(muteButton)}`,
    [
      describeTimelinePreviewState(preview),
      describeAudioGainHandles(),
      `gain values: ${gainValues.join(', ') || 'none'}`,
      `native audio: ${nativeStates.join(', ') || 'none'}`,
    ],
  )
}

const CHECKS: ReplayCheckDefinition[] = [
  {
    id: 'canvas-graph-created',
    title: 'Canvas graph created',
    action: 'Wait for the replay object, location, video, audio, and editor nodes plus their required edges.',
    expected: 'Replay object/location/video/audio/editor nodes exist, and the video/audio nodes are wired to the editor.',
    run: runCanvasGraphCreated,
  },
  {
    id: 'deleted-node-edge-cleanup',
    title: 'Deleted node leaves no dangling edges',
    action: 'Wait for the deletion probe node to disappear, then inspect all canvas edge endpoints.',
    expected: 'No edge source or target references a missing node after the replay removes its probe node.',
    run: runDeletedNodeEdgeCleanup,
  },
  {
    id: 'canvas-tab-create-delete',
    title: 'Canvas tab create/delete works',
    action: 'Click the canvas tab plus button, create a temporary canvas, return to the main canvas, then delete the temporary tab.',
    expected: 'The temporary canvas appears in the tab bar and backend canvas list, then disappears from both after delete confirmation.',
    run: runCanvasTabCreateDelete,
  },
  {
    id: 'director-created-canvas-handoff',
    title: 'Director-created canvas tab syncs live',
    action: 'Wait for the Director create_canvas tool to create Canvas 2 from replay source nodes.',
    expected: 'Canvas 2 appears in the tab bar without refresh and is persisted in the backend canvas list.',
    run: runDirectorCreatedCanvasHandoff,
  },
  {
    id: 'jsonb-load-validator',
    title: 'Backend JSONB load validator passes',
    action: 'Load the persisted JSONB projection and run the backend YDoc validator.',
    expected: 'The backend materializes JSONB rows into a valid YDoc with no dangling edge endpoints.',
    run: runJsonbLoadValidator,
  },
  {
    id: 'mocked-assets-completed',
    title: 'Mocked video/audio assets completed',
    action: 'Wait for generated video media and editor reference cards.',
    expected: 'Generated video has a src and the editor has video/audio references.',
    run: runMockedAssetsCompleted,
  },
  {
    id: 't277237593-project-leaves-draft',
    title: 'T277237593: Generated video project leaves Draft',
    action: 'Read the replay project after mocked video generation completes.',
    expected: 'The project status is no longer Draft.',
    run: runProjectLeavesDraft,
  },
  {
    id: 't276778859-object-location-grid-layout',
    title: 'T276778859: Object grid does not overlap Location grid',
    action: 'Measure replay_object_1 and replay_location_1 in flow coordinates.',
    expected: 'The rendered object and location node rectangles do not intersect.',
    run: runObjectLocationGridLayout,
  },
  {
    id: 't276931527-settings-my-video-model',
    title: 'T276931527: Settings shows My Video Model',
    action: 'Open Project Settings from the canvas toolbar.',
    expected: 'The modal exposes the My Video Model section and Add Model control.',
    run: runSettingsMyVideoModel,
  },
  {
    id: 't277033827-byom-edit-key-state',
    title: 'T277033827: Edit Model shows current key state',
    action: 'Seed a replay BYOM model, open Project Settings, then open Edit Model.',
    expected: 'Current App Key is masked/non-blank and New App Key is blank.',
    run: runByomEditKeyState,
  },
  {
    id: 't277240403-video-config-my-model-chip',
    title: 'T277240403: Video Config shows default My Model chip',
    action: 'Open the Video Config flyout after seeding a default replay BYOM model.',
    expected: 'The My Video Model section contains the replay model chip with a star.',
    run: runVideoConfigMyModelChip,
  },
  {
    id: 'generated-video-node-playback',
    title: 'Generated video node can play',
    action: 'Focus the generated video node and click its Play control.',
    expected: 'The video receives playback and the control changes to Pause.',
    run: runGeneratedVideoNodePlayback,
  },
  {
    id: 'video-editor-references',
    title: 'Video Editor has video + audio references',
    action: 'Inspect the Video Editor reference rail.',
    expected: 'The rail contains at least one video reference and one audio reference.',
    run: runVideoEditorReferences,
  },
  {
    id: 't277027053-video-config-hidden-while-generating',
    title: 'T277027053: Busy video node hides editable Config panel',
    action: 'Flip replay_video_1 to running via YDoc while selected, then click QuickInfo Config.',
    expected: 'The editable Video Config panel is hidden and cannot be reopened while running.',
    run: runVideoConfigHiddenWhileGenerating,
  },
  {
    id: 't277192495-video-editor-timeline-track-scroll',
    title: 'T277192495: Timeline Editor tracks stay aligned while scrolling',
    action: 'Open the Video Editor, add video/audio tracks, then wheel over the timeline body.',
    expected: 'The shared wrapper scrolls vertically, the body does not capture vertical scroll, and track headers remain aligned.',
    run: runVideoEditorTimelineTrackScroll,
  },
  {
    id: 'video-editor-preview-audio',
    title: 'Video Editor preview can unmute and play audio',
    action: 'Unmute the Video Editor preview and click Play.',
    expected: 'The preview plays while an audio clip has positive WebAudio gain.',
    run: runVideoEditorPreviewAudio,
  },
]

function initialCheckStates(): ReplayCheckState[] {
  return CHECKS.map((check) => ({
    id: check.id,
    title: check.title,
    action: check.action,
    expected: check.expected,
    status: 'pending',
    details: [],
  }))
}

function statusClassName(status: ReplayCheckStatus): string {
  if (status === 'passed') return 'border-success/40 bg-success/10 text-success'
  if (status === 'failed') return 'border-destructive/40 bg-destructive/10 text-destructive'
  if (status === 'blocked') return 'border-warning/40 bg-warning/10 text-warning'
  if (status === 'running') return 'border-primary/40 bg-primary/10 text-primary'
  return 'border-border bg-surface-1 text-muted-foreground'
}

function StatusIcon({ status }: { status: ReplayCheckStatus }) {
  if (status === 'passed') return <CheckCircle2 className="h-4 w-4" />
  if (status === 'failed') return <XCircle className="h-4 w-4" />
  if (status === 'blocked') return <AlertTriangle className="h-4 w-4" />
  if (status === 'running') return <Loader2 className="h-4 w-4 animate-spin" />
  return <Play className="h-4 w-4" />
}

function normalizeError(error: Error): ReplayCheckResult {
  if (error instanceof ReplayCheckError) {
    return {
      status: error.status,
      observed: error.observed,
      details: error.details.length > 0 ? error.details : [error.message],
    }
  }
  return {
    status: 'failed',
    observed: error.message,
    details: ['Unexpected replay runner error.'],
  }
}

async function postReplayLog(
  projectId: string,
  check: ReplayCheckDefinition,
  result: ReplayCheckResult,
): Promise<void> {
  await apiFetch('/api/debug-log', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ts: Date.now(),
      ns: 'dw:dashboard-realtime-replay',
      args: [{
        event: `in-app-${check.id}-${result.status}`,
        feature: 'dashboard-realtime-replay',
        concern: 'in-app-replay-operation',
        project_id: projectId,
        check_id: check.id,
        status: result.status,
        observed: result.observed,
        details: result.details ?? [],
      }],
    }),
    keepalive: true,
  }).catch(() => undefined)
}

export const ReplayOperationCards = memo(function ReplayOperationCards({
  enabled,
  projectId,
  canvasNodes,
  canvasEdges,
  onCreateCanvas,
  onDeleteCanvas,
  onFocusNode,
  onCardsUpdated,
}: ReplayOperationCardsProps) {
  const [checks, setChecks] = useState<ReplayCheckState[]>(initialCheckStates)
  const runGenerationRef = useRef(0)
  const autoStartedRef = useRef(false)
  const canvasNodeIds = useMemo(
    () => new Set((canvasNodes ?? []).map((node) => node.id)),
    [canvasNodes],
  )
  const canvasNodesRef = useRef(canvasNodes ?? [])
  const canvasNodeIdsRef = useRef(canvasNodeIds)
  const canvasEdgesRef = useRef(canvasEdges ?? [])
  const { t } = useTranslation('workspace')

  useEffect(() => {
    canvasNodesRef.current = canvasNodes ?? []
  }, [canvasNodes])

  useEffect(() => {
    canvasNodeIdsRef.current = canvasNodeIds
  }, [canvasNodeIds])

  useEffect(() => {
    canvasEdgesRef.current = canvasEdges ?? []
  }, [canvasEdges])

  const setCheck = useCallback((id: ReplayCheckId, patch: Partial<ReplayCheckState>) => {
    setChecks((previous) =>
      previous.map((check) =>
        check.id === id
          ? { ...check, ...patch, updatedAt: Date.now() }
          : check,
      ),
    )
  }, [])

  const runFromIndex = useCallback(async (startIndex: number) => {
    const generation = runGenerationRef.current + 1
    runGenerationRef.current = generation
    setChecks((previous) =>
      previous.map((check, index) => {
        if (index < startIndex) return check
        return {
          ...check,
          status: index === startIndex ? 'running' : 'pending',
          observed: undefined,
          details: [],
          updatedAt: Date.now(),
        }
      }),
    )

    const context: ReplayRunnerContext = {
      projectId,
      getCanvasNodeIds: () => canvasNodeIdsRef.current,
      getCanvasNode: (nodeId: string) => canvasNodesRef.current.find((node) => node.id === nodeId),
      getCanvasEdges: () => canvasEdgesRef.current,
      onCreateCanvas,
      onDeleteCanvas,
      onFocusNode,
    }

    for (let index = startIndex; index < CHECKS.length; index += 1) {
      if (runGenerationRef.current !== generation) return
      const check = CHECKS[index]
      setCheck(check.id, { status: 'running', observed: undefined, details: [] })
      const result = await runCheckWithTimeout(check, context).catch(normalizeError)
      await postReplayLog(projectId, check, result)
      if (runGenerationRef.current !== generation) return
      setCheck(check.id, {
        status: result.status,
        observed: result.observed,
        details: result.details ?? [],
      })
      if (result.status !== 'passed') return
    }
  }, [onCreateCanvas, onDeleteCanvas, onFocusNode, projectId, setCheck])

  useEffect(() => {
    if (!enabled || autoStartedRef.current) return
    autoStartedRef.current = true
    void runFromIndex(0)
  }, [enabled, runFromIndex])

  useEffect(() => {
    if (!enabled) return
    onCardsUpdated?.()
  }, [checks, enabled, onCardsUpdated])

  if (!enabled) return null

  const running = checks.some((check) => check.status === 'running')
  const suiteStatus = checks.some((check) => check.status === 'failed')
    ? 'failed'
    : checks.some((check) => check.status === 'blocked')
      ? 'blocked'
      : checks.every((check) => check.status === 'passed')
        ? 'passed'
        : running
          ? 'running'
          : 'pending'

  return (
    <div
      className="space-y-2"
      data-testid="replay-operation-cards"
      data-replay-suite-status={suiteStatus}
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-foreground">{t('replayChecks.title')}</div>
          <div className="text-xs font-medium text-muted-foreground">{t('replayChecks.description')}</div>
        </div>
        <button
          type="button"
          onClick={() => void runFromIndex(0)}
          disabled={running}
          className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-input-border bg-surface-1 px-2 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="replay-suite-rerun"
          title={t('replayChecks.replayAllTitle')}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {t('replayChecks.replayAll')}
        </button>
      </div>

      {checks.map((check, index) => (
        <div
          key={check.id}
          className="rounded-lg border border-border-subtle bg-surface-0 p-3"
          data-testid={`replay-check-card-${check.id}`}
          data-replay-check-id={check.id}
          data-replay-check-status={check.status}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${statusClassName(check.status)}`}
                  data-testid={`replay-check-status-${check.id}`}
                >
                  <StatusIcon status={check.status} />
                  {t(`replayChecks.status.${check.status}`)}
                </span>
                <span className="text-sm font-medium text-foreground">{t(`replayChecks.checks.${check.id}.title`)}</span>
              </div>
              <div className="mt-2 space-y-1 text-xs font-medium text-muted-foreground">
                <div><span className="text-foreground/80">{t('replayChecks.actionLabel')}</span> {t(`replayChecks.checks.${check.id}.action`)}</div>
                <div><span className="text-foreground/80">{t('replayChecks.expectedLabel')}</span> {t(`replayChecks.checks.${check.id}.expected`)}</div>
                {check.observed && (
                  <div data-testid={`replay-check-observed-${check.id}`}>
                    <span className="text-foreground/80">{t('replayChecks.observedLabel')}</span> {check.observed}
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => void runFromIndex(index)}
              disabled={running}
              className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-lg border border-input-border bg-surface-1 px-2 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`replay-check-rerun-${check.id}`}
              title={t('replayChecks.replayThisCheck')}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {t('replayChecks.replay')}
            </button>
          </div>

          {check.details.length > 0 && (
            <details className="mt-2 text-xs font-medium text-muted-foreground">
              <summary className="cursor-pointer text-foreground/80">{t('replayChecks.details')}</summary>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                {check.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      ))}
    </div>
  )
})
